import { useEffect, useMemo, useState } from 'react';
import { Bell, BellOff, Activity, AlertTriangle, Users } from 'lucide-react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { MetricCard } from '@/components/molecules/MetricCard';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/molecules/Card';
import { Select } from '@/components/atoms/Select';
import { Badge } from '@/components/atoms/Badge';
import { Spinner } from '@/components/atoms/Spinner';
import { DataTable } from '@/components/organisms/DataTable';
import type { DataTableColumn } from '@/components/organisms/DataTable/DataTable.types';
import { getNotificationsHealth } from '@/services/api/backoffice.api';
import type {
  NotificationsHealth,
  NotificationsHealthPerJob,
  NotificationsHealthTypeCount,
} from '@/types/backoffice.types';
import styles from './NotificationsHealthPage.module.scss';

const WINDOW_OPTIONS = [
  { label: 'Últimos 7 días', value: '7' },
  { label: 'Últimos 30 días', value: '30' },
  { label: 'Últimos 90 días', value: '90' },
];

// Etiquetas legibles para cada tipo de notificación
const TYPE_LABELS: Record<string, string> = {
  study_reminder: 'Recordatorio de estudio',
  weekly_assignment: 'Asignación semanal',
  streak_at_risk: 'Racha en peligro',
  daily_streak: 'Racha diaria conseguida',
  weekly_goal_achieved: 'Reto semanal conseguido',
  streak_milestone: 'Hito de racha',
  rank_close_to_top: 'Cerca del #1 del ranking',
  rank_lost_first: 'Te quitaron el #1',
  level_up: 'Subida de nivel',
  achievement_unlocked: 'Logro desbloqueado',
};

const JOB_LABELS: Record<string, string> = {
  studyReminder: 'Recordatorio de estudio',
  weeklyAssignmentReminder: 'Asignación semanal',
  streakAtRisk: 'Racha en peligro',
};

const SIGNAL_VARIANT: Record<'green' | 'yellow' | 'red', 'success' | 'warning' | 'error'> = {
  green: 'success',
  yellow: 'warning',
  red: 'error',
};

const SIGNAL_LABEL: Record<'green' | 'yellow' | 'red', string> = {
  green: 'Saludable',
  yellow: 'Atención',
  red: 'Migrar ya',
};

export function NotificationsHealthPage() {
  const [days, setDays] = useState<number>(30);
  const [data, setData] = useState<NotificationsHealth | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    setError(null);
    getNotificationsHealth(days)
      .then((payload) => {
        if (!cancelled) setData(payload);
      })
      .catch((err) => {
        if (!cancelled) {
          console.error('Failed to load notifications health', err);
          setError('No se pudo cargar la información del dashboard.');
        }
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [days]);

  const chartData = useMemo(() => {
    if (!data) return [];
    return data.dailyTimeseries.map((p) => ({
      day: formatDayLabel(p.day),
      sent: p.sent,
      errors: p.errors,
    }));
  }, [data]);

  const jobColumns: DataTableColumn<NotificationsHealthPerJob>[] = useMemo(
    () => [
      {
        key: 'jobName',
        header: 'Job',
        accessor: (row) => JOB_LABELS[row.jobName] ?? row.jobName,
      },
      { key: 'runs', header: 'Ejecuciones', accessor: (row) => row.runs },
      {
        key: 'candidates',
        header: 'Candidatos (avg / max)',
        accessor: (row) => `${row.candidatesAvg} / ${row.candidatesMax}`,
      },
      { key: 'ok', header: 'OK (avg)', accessor: (row) => row.okAvg },
      { key: 'errors', header: 'Errores (avg)', accessor: (row) => row.errorAvg },
      {
        key: 'duration',
        header: 'Duración ms (avg / max)',
        accessor: (row) => `${row.durationMsAvg} / ${row.durationMsMax}`,
      },
    ],
    []
  );

  const typeColumns: DataTableColumn<NotificationsHealthTypeCount>[] = useMemo(
    () => [
      {
        key: 'type',
        header: 'Tipo',
        accessor: (row) => TYPE_LABELS[row.type] ?? row.type,
      },
      { key: 'count7d', header: 'Últimos 7 días', accessor: (row) => row.count7d, align: 'right' },
      {
        key: 'count30d',
        header: 'Últimos 30 días',
        accessor: (row) => row.count30d,
        align: 'right',
      },
    ],
    []
  );

  return (
    <div className={styles.health}>
      <header className={styles.health__header}>
        <div className={styles.health__headerMain}>
          <h1 className={styles.health__title}>Salud de notificaciones</h1>
          <p className={styles.health__subtitle}>
            Estado del sistema de push notifications: volumen, errores y momento óptimo para migrar
            a Cloudflare Queues.
          </p>
        </div>
        <div className={styles.health__headerActions}>
          {data && (
            <Badge variant={SIGNAL_VARIANT[data.migrationSignal.level]} size="lg">
              {SIGNAL_LABEL[data.migrationSignal.level]}
            </Badge>
          )}
          <Select
            value={String(days)}
            onValueChange={(value) => setDays(Number(value))}
            options={WINDOW_OPTIONS}
          />
        </div>
      </header>

      {isLoading && (
        <div className={styles.health__loading}>
          <Spinner />
        </div>
      )}

      {error && !isLoading && (
        <Card>
          <CardContent>
            <p className={styles.health__error}>{error}</p>
          </CardContent>
        </Card>
      )}

      {data && !isLoading && (
        <>
          {data.migrationSignal.level !== 'green' && (
            <Card className={styles.health__callout} data-level={data.migrationSignal.level}>
              <CardContent>
                <div className={styles.health__calloutInner}>
                  <AlertTriangle aria-hidden="true" />
                  <div>
                    <strong>
                      {data.migrationSignal.level === 'red'
                        ? 'Migración a Cloudflare Queues recomendada'
                        : 'Vigila los próximos días'}
                    </strong>
                    <p>{data.migrationSignal.reason}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          <section className={styles.health__metrics} aria-label="Métricas resumen">
            <MetricCard
              icon={Bell}
              label="Pushes enviados"
              value={data.summary.totalSent.toLocaleString('es-ES')}
              description={`Últimos ${data.windowDays} días`}
              variant="primary"
            />
            <MetricCard
              icon={BellOff}
              label="Pushes con error"
              value={data.summary.totalErrors.toLocaleString('es-ES')}
              description={`Últimos ${data.windowDays} días`}
              variant={data.summary.totalErrors > 0 ? 'warning' : 'default'}
            />
            <MetricCard
              icon={Activity}
              label="Tasa de error (7d)"
              value={`${data.summary.errorRatePct.toFixed(2)}%`}
              description="Sobre últimos 7 días"
              variant={data.summary.errorRatePct > 1 ? 'warning' : 'success'}
            />
            <MetricCard
              icon={Users}
              label="Alumnos suscritos"
              value={`${formatSubscribedPct(
                data.subscriptions.activeUsersWithPush,
                data.subscriptions.totalStudents
              )}%`}
              description={`${data.subscriptions.activeUsersWithPush} de ${data.subscriptions.totalStudents} alumnos`}
              variant="default"
            />
          </section>

          <Card>
            <CardHeader>
              <CardTitle>Evolución diaria</CardTitle>
            </CardHeader>
            <CardContent>
              {chartData.length === 0 ? (
                <p className={styles.health__emptyChart}>
                  Sin datos suficientes en la ventana seleccionada.
                </p>
              ) : (
                <div className={styles.health__chartWrapper}>
                  <ResponsiveContainer width="100%" height={280}>
                    <LineChart data={chartData} margin={{ top: 8, right: 16, bottom: 0, left: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--color-neutral-200)" />
                      <XAxis dataKey="day" stroke="var(--color-neutral-500)" fontSize={12} />
                      <YAxis stroke="var(--color-neutral-500)" fontSize={12} allowDecimals={false} />
                      <Tooltip />
                      <Legend />
                      <Line
                        type="monotone"
                        dataKey="sent"
                        name="Enviados"
                        stroke="#3b82f6"
                        strokeWidth={2}
                        dot={false}
                      />
                      <Line
                        type="monotone"
                        dataKey="errors"
                        name="Errores"
                        stroke="#ef4444"
                        strokeWidth={2}
                        dot={false}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Por tipo de notificación</CardTitle>
            </CardHeader>
            <CardContent>
              <DataTable
                columns={typeColumns}
                data={data.byType}
                emptyMessage="Sin notificaciones registradas en la ventana."
              />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Cron jobs</CardTitle>
            </CardHeader>
            <CardContent>
              <DataTable
                columns={jobColumns}
                data={data.perJob}
                emptyMessage="Aún no hay ejecuciones registradas. El primer tick del cron horario las creará."
              />
            </CardContent>
          </Card>

        </>
      )}
    </div>
  );
}

function formatSubscribedPct(subscribed: number, total: number): string {
  if (total === 0) return '0';
  return ((subscribed / total) * 100).toFixed(1);
}

function formatDayLabel(yyyyMmDd: string): string {
  const [, month, day] = yyyyMmDd.split('-');
  if (!month || !day) return yyyyMmDd;
  return `${day}/${month}`;
}

export default NotificationsHealthPage;
